import { Link } from "react-router-dom";
import { ArrowUpRight, Heart, Users, Compass, Lightbulb, ShieldCheck } from "lucide-react";
import { usePageSEO } from "@/lib/seo";

const waarden = [
  {
    icon: Heart,
    label: "Mens centraal",
    body: "Achter elke situatie zit een verhaal. Wij sluiten aan bij de persoon, niet bij het protocol.",
  },
  {
    icon: Users,
    label: "Kennis én ervaring",
    body: "Professionele deskundigheid en ervaringsdeskundigheid werken bij ons naast elkaar.",
  },
  {
    icon: Compass,
    label: "Wendbaar",
    body: "We werken in projecten en pilots — klein starten, scherp evalueren, opschalen wat werkt.",
  },
  {
    icon: Lightbulb,
    label: "Gelijke kansen",
    body: "Omstandigheden mogen niet bepalen hoeveel toekomstperspectief iemand heeft.",
  },
  {
    icon: ShieldCheck,
    label: "Zorgvuldig",
    body: "Vertrouwelijk, privacy-bewust en met oog voor wat een traject van iemand vraagt.",
  },
];

export default function OverFinPage() {
  usePageSEO({
    title: "Over FIN | Stichting FIN",
    description:
      "Stichting FIN (Future Is Now) is een Nederlandse maatschappelijke stichting in Rotterdam die werkt aan bestaanszekerheid, zelfredzaamheid en gelijke kansen.",
  });

  return (
    <div data-testid="over-fin-page">
      {/* Hero */}
      <section className="relative bg-petrol text-cream overflow-hidden">
        <div aria-hidden="true" className="absolute -top-32 -right-24 w-[500px] h-[500px] rounded-full bg-gold/15 blur-3xl" />
        <div className="container-x pt-20 md:pt-24 pb-14 max-w-4xl relative">
          <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-gold mb-5">Over FIN</p>
          <h1 className="font-display text-[clamp(2.4rem,6vw,4.6rem)] leading-[1.02] tracking-tight text-balance">
            Future Is <em className="italic text-gold">Now</em>.
          </h1>
          <p className="mt-6 text-lg leading-relaxed text-cream/85 text-pretty">
            Verandering hoeft niet te wachten op het perfecte moment. Soms begint vooruitgang met één gesprek, één praktische
            stap of één persoon die besluit naast iemand te gaan staan.
          </p>
        </div>
      </section>

      {/* Missie & visie */}
      <section className="py-20 md:py-24 bg-cream">
        <div className="container-x grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-gold mb-4">Onze missie</p>
            <h2 className="font-display text-3xl md:text-4xl leading-tight tracking-tight text-balance text-petrol mb-5">
              Bestaanszekerheid, zelfredzaamheid en <em className="italic text-gold">gelijke kansen</em>.
            </h2>
            <p className="text-[17px] leading-[1.75] text-inkMuted text-pretty">
              Stichting FIN zet zich in voor het vergroten van bestaanszekerheid, zelfredzaamheid en gelijke kansen voor mensen
              en gemeenschappen in kwetsbare omstandigheden. Wij doen dat via persoonlijke ondersteuning én via maatschappelijke
              projecten.
            </p>
          </div>
          <div className="lg:col-span-6">
            <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-gold mb-4">Onze visie</p>
            <h2 className="font-display text-3xl md:text-4xl leading-tight tracking-tight text-balance text-petrol mb-5">
              Problemen niet alleen oplossen. <em className="italic text-gold">Waar mogelijk voorkomen.</em>
            </h2>
            <p className="text-[17px] leading-[1.75] text-inkMuted text-pretty">
              Wij geloven in een samenleving waarin omstandigheden niet bepalen hoeveel toekomstperspectief iemand heeft. Door
              praktische ondersteuning te combineren met preventie en maatschappelijke projecten willen we niet alleen
              symptomen bestrijden, maar structureel bijdragen aan een sterkere samenleving.
            </p>
          </div>
        </div>
      </section>

      {/* Waarden */}
      <section className="py-20 md:py-24 bg-sand">
        <div className="container-x">
          <div className="max-w-3xl mb-12">
            <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-gold mb-4">Onze waarden</p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight text-balance text-petrol">
              Vijf waarden. <em className="italic text-gold">Elke dag.</em>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {waarden.map((w) => {
              const Icon = w.icon;
              return (
                <article
                  key={w.label}
                  className="bg-ivory border border-line rounded-3xl p-7 hover:shadow-card transition"
                  data-testid={`waarde-${w.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <span className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-goldSoft text-petrol mb-4">
                    <Icon size={22} strokeWidth={1.75} />
                  </span>
                  <h3 className="font-display text-2xl text-petrol leading-tight mb-2">{w.label}</h3>
                  <p className="text-inkMuted leading-relaxed text-[15px] text-pretty">{w.body}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Impact & transparency */}
      <section className="py-20 md:py-24 bg-cream">
        <div className="container-x grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-gold mb-4">Impact & transparantie</p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight text-balance text-petrol">
              Eerlijk over wat we <em className="italic text-gold">(nog niet) weten</em>.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-[17px] leading-[1.75] text-inkMuted text-pretty">
            <p>
              Stichting FIN is een jonge stichting. We hechten aan eerlijke communicatie: we delen geen impactcijfers zolang die
              er niet zorgvuldig zijn. Wél leggen we uit wát we meten en waarom — kwaliteit van ondersteuning, voortgang van
              trajecten, bereik van projecten en geleerde lessen.
            </p>
            <p>
              Zodra pilots voldoende omvang hebben, publiceren we geanonimiseerde inzichten. Zo bouwen we impact die verder
              gaat dan een dashboard.
            </p>
          </div>
        </div>
      </section>

      {/* Bestuur & Organisatie placeholder */}
      <section className="py-20 md:py-24 bg-sand">
        <div className="container-x">
          <div className="max-w-3xl mb-8">
            <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-gold mb-4">Organisatie</p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight text-balance text-petrol">
              Bestuur, professionals & vrijwilligers.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="bg-ivory border border-line rounded-3xl p-7">
              <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-gold mb-3">In opbouw</p>
              <p className="font-display text-2xl text-petrol leading-tight mb-2">Team & bestuur</p>
              <p className="text-inkMuted text-[15px] leading-relaxed">
                Ons team bestaat uit professionals (HBO/WO-opgeleid) en ervaringsdeskundigen. Zodra alle formele stappen rond
                zijn, publiceren we hier bestuurssamenstelling, jaarverslagen en beleidsplan.
              </p>
            </div>
            <div className="bg-ivory border border-line rounded-3xl p-7">
              <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-gold mb-3">Verantwoording</p>
              <p className="font-display text-2xl text-petrol leading-tight mb-2">Jaarverslagen & beleid</p>
              <p className="text-inkMuted text-[15px] leading-relaxed">
                Zodra beschikbaar publiceren wij jaarverslag, beleidsplan en (indien van toepassing) ANBI-informatie op deze
                pagina.
              </p>
            </div>
          </div>
          <p className="mt-6 text-inkMuted/80 text-sm italic">
            KvK-nummer: 89903293 · Rotterdam · Landelijk actief
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24 bg-cream">
        <div className="container-x">
          <div className="bg-petrol text-cream rounded-3xl p-10 md:p-14 relative overflow-hidden">
            <div aria-hidden="true" className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-gold/20 blur-3xl" />
            <div className="relative max-w-3xl">
              <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-gold mb-4">Kennismaken?</p>
              <h2 className="font-display text-3xl md:text-4xl leading-tight tracking-tight text-balance mb-4">
                Wij denken graag met u mee.
              </h2>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-gold text-petrol hover:bg-goldSoft transition rounded-full px-6 py-3 text-sm font-semibold shadow-gold"
                  data-testid="over-cta-contact"
                >
                  Plan een kennismaking <ArrowUpRight size={15} />
                </Link>
                <Link
                  to="/wat-we-doen"
                  className="inline-flex items-center gap-2 bg-cream/10 border border-cream/20 text-cream hover:bg-cream/15 transition rounded-full px-6 py-3 text-sm font-semibold"
                >
                  Bekijk wat we doen
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
